import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch } from 'react-redux';

// material-ui
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CustomTooltip from 'components/CustomTooltip';
// third-party
import * as Yup from 'yup';
import { FieldArray, FormikProvider, FormikValues, useFormik, setNestedObjectValues } from 'formik';

import { openSnackbar } from 'store/reducers/snackbar';
import { get } from 'lodash';

import {
  Commission,
  CommissionsForMcaDocument,
  Company,
  ConfigFee,
  ConfigFeeName,
  FundingFee,
  Integration,
  IntegrationType,
  Underwriter,
  McaRefi,
  McaRefiDto,
  MerchantCashAdvance,
  MerchantCashAdvanceStatus,
  NonWorkingDay,
  Recurrency,
  RecurrencyState,
  useConfigFeesQuery,
  useDeleteCommissionMutation,
  useDeleteFeeMutation,
  useIntegrationsQuery,
  User,
  useSaveCommissionMutation,
  useSaveFeeMutation,
  useSaveMerchantCashAdvanceMutation,
  useSaveRecurrencyMutation,
  useSaveFundingPipelineMcaMutation,
  useMcaDisplayAttributesQuery,
  useApplicationsForMerchantLazyQuery,
  useLinkActiveOfferToMcaMutation,
  Offer,
  OfferByMcaIdDocument
} from '../../graphql/types';
import AnimateButton from '../../components/@extended/AnimateButton';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import AddMerchantCashAdvanceConfirm from './AddMerchantCashAdvanceConfirm';
import { MerchantAutocomplete } from './MerchantAutocomplete';
import CurrencyFormatterWithoutPrefix from '../../components/input-formatters/CurrencyFormatterWithoutPrefix';
import PercentFormatterWithoutPrefix from 'components/input-formatters/PercentFormatterWithoutPrefix';
import { InternalCompanyAutocomplete } from '../companies/InternalCompanyAutocomplete';
import {
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  FlagOutlined,
  PlusCircleOutlined,
  PercentageOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { FunderAutocomplete } from '../companies/FunderAutocomplete';
import { SecondaryFunderAutocomplete } from '../companies/SecondaryFunderAutocomplete';
import { BrokerAutocomplete } from '../users/BrokerAutocomplete';
import useConfig from '../../hooks/useConfig';
import { MerchantBankAccountInput } from './MerchantBankAccountInput';
import { isValidRoutingNumber } from '../../utils/bankAccounts';
import {
  addResolvedConfigToItems,
  createPresetFeeCommissionRow,
  FeeCommissionFormRow,
  getConfigFees,
  getConfigCommissions
} from '../../utils/feesCommissions';
import { IntegrationAutocomplete } from '../integrations/IntegrationAutocomplete';
import { v4 as uuid4 } from 'uuid';
import { PercentText } from '../../components/input-formatters/PercentText';
import Decimal from 'decimal.js';
import { SetMcaType } from './SetMcaType';
import { SetMcaRefis } from './SetMcaRefis';
import { McaRefiLevelField, McaStackLevelField } from './McaDisplayTierFields';
import { McaDisplayAttributeType, parseMcaDisplayTiers } from '../../utils/mca';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import NumberFormatterWithoutPrefix from 'components/input-formatters/NumberFormatterWithoutPrefix';
import { IsoManagerAutocomplete } from '../users/IsoManagerAutocomplete';
import { getRefisTotal } from './McaRefisListOption';
import { useTheme } from '@mui/material/styles';
import { LegalUserAutocomplete } from '../users/LegalUserAutocomplete';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getProcessorStartDate } from '../../utils/getProcessorStartDate';
import { getErrorMessage, requireMutationData } from '../../utils/graphqlMutationResult';
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

// ==============================|| MERCHANT ADD / EDIT / DELETE ||============================== //

export interface Props {
  open: boolean;
  isFundingPipeline?: boolean;
  handleDrawerOpen: () => void;
  entity?: MerchantCashAdvance | undefined;
  onSave: (merchantCashAdvance: MerchantCashAdvance, openRecurrencyAchDetails: boolean) => void;
  nonWorkingDays: NonWorkingDay[];
  applicationId?: string;
  offerId?: string;
  isRefiMode?: boolean;
}

export interface Values {
  isImport: boolean;
  integration: Integration;
  status: MerchantCashAdvanceStatus;
  state: RecurrencyState;
  merchant: Company;
  funder: Underwriter;
  secondaryFunders: {
    [id: string]: {
      lender: Underwriter;
    };
  };
  company: Company;
  broker: User;
  manager: User;
  legal: User;
  accountNumber: string;
  routingNumber: string;
  amount: number;
  term: number;
  factorRate: number;
  startDate: Date;
  fundingDate: Date;
  commissionPercentage: number;
  fees: {
    [id: string]: {
      amount: number;
      percents: number;
      comment: string;
      config: ConfigFee;
    };
  };
  commissions: {
    [id: string]: {
      amount: number;
      isPaid: boolean;
      percents: number;
      comment: string;
      config: ConfigFee;
    };
  };
  isRefi: boolean;
  refis?: McaRefiDto[];
  refiLevel: number | null;
  stackLevel: number | null;
  reverseFunding: boolean;
  linkedApplicationId: string;
}

const AddMerchantCashAdvance = ({ open, handleDrawerOpen, entity, onSave, nonWorkingDays, isFundingPipeline = false, applicationId, offerId, isRefiMode = false }: Props) => {
  const { mode } = useConfig();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isCreating = !entity || isFundingPipeline || isRefiMode;
  const [saveMerchantCashAdvance] = useSaveMerchantCashAdvanceMutation();
  const [linkActiveOfferToMca] = useLinkActiveOfferToMcaMutation();
  const [saveFundingPipelineMca] = useSaveFundingPipelineMcaMutation();
  const [saveFee] = useSaveFeeMutation();
  const [deleteFee] = useDeleteFeeMutation();
  const [saveCommission] = useSaveCommissionMutation();
  const [deleteCommission] = useDeleteCommissionMutation();
  const [saveRecurrency] = useSaveRecurrencyMutation();
  const [startDateChanged, setStartDateChanged] = useState<boolean>(false);
  const rowSx = {
    backgroundColor: mode === 'dark' ? '#3B414F' : '#e6ebf1',
    padding: 2,
    borderBottom: '1px solid',
    borderColor: '#656974'
  };

  const getNextBusinessDay = (start: Date) => {
    let minDate = dayjs(start);
    // eslint-disable-next-line no-loop-func
    while ([6, 0].includes(minDate.day()) || nonWorkingDays.some((d) => dayjs(d.date).isSame(minDate, 'date'))) {
      minDate = minDate.add(1, 'day');
    }
    return minDate.toDate();
  };
  const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).toDate();
  const tomorrow = dayjs().set('hour', 0).set('minute', 0).set('second', 0).add(1, 'day').toDate();
  const nextBusinessDay = getNextBusinessDay(tomorrow);
  const { data: mcaDisplayAttributesData } = useMcaDisplayAttributesQuery({ fetchPolicy: 'network-only' });
  const mcaDisplayAttributeRows = mcaDisplayAttributesData?.mcaDisplayAttributes || [];
  const getStartDateColor = (startDate: Date | null): string => {
    if (startDate && dayjs(startDate).isSame(today, 'date')) {
      return '#FF7A45';
    } else if (startDate && dayjs(startDate).isSame(nextBusinessDay, 'date')) {
      return '#52C41A';
    }
    return 'inherit';
  };

  const getInitialValues = (entity: FormikValues | undefined): Values => {
    const newEntity = {
      isImport: false,
      integration: { id: '' } as Integration,
      status: MerchantCashAdvanceStatus.Open,
      state: RecurrencyState.New,
      merchant: { id: '' } as Company,
      funder: {} as Underwriter,
      secondaryFunders: {} as {
        [id: string]: {
          lender: Underwriter;
        };
      },
      company: { id: '' } as Company,
      broker: {} as User,
      manager: {} as User,
      legal: {} as User,
      accountNumber: '',
      routingNumber: '',
      amount: 1000,
      term: 10,
      factorRate: 1.499,
      startDate: nextBusinessDay,
      fundingDate: today,
      commissionPercentage: 10,
      fees: {} as {
        [id: string]: {
          amount: number;
          percents: number;
          comment: string;
          config: ConfigFee;
        };
      },
      commissions: {} as {
        [id: string]: {
          amount: number;
          percents: number;
          comment: string;
          config: ConfigFee;
        };
      },
      isRefi: false,
      refis: [] as McaRefiDto[],
      refiLevel: null as number | null,
      stackLevel: null as number | null,
      reverseFunding: false,
      linkedApplicationId: ''
    };
    if (entity) {
      newEntity.isImport = false;
      newEntity.state = entity.state;
      newEntity.integration = entity.integration;
      newEntity.merchant = entity.merchant;
      newEntity.funder = entity.funder;
      newEntity.secondaryFunders = entity?.secondaryFunders?.reduce(
        (funders: { [id: string]: any }, funder: Underwriter) => ({
          ...funders,
          [uuid4()]: {
            lender: funder
          }
        }),
        {}
      );
      newEntity.company = entity.company;
      newEntity.broker = entity?.broker;
      newEntity.manager = entity?.manager;
      newEntity.legal = entity?.legal;
      newEntity.accountNumber = entity.accountNumber || '';
      newEntity.routingNumber = entity.routingNumber || '';
      newEntity.amount = entity.amount;
      newEntity.term = entity.term;
      newEntity.factorRate = entity.factorRate;
      newEntity.commissionPercentage = entity.commissionPercentage;
      const tiers = parseMcaDisplayTiers(entity.displayAttributes || [], mcaDisplayAttributeRows);
      newEntity.stackLevel = tiers.stack;
      newEntity.reverseFunding = tiers.reverseFunding;
      if (isRefiMode) {
        newEntity.isRefi = true;
        newEntity.stackLevel = null;
        const isIntegrationActive = entity.integration?.autostart === true;
        if (!isIntegrationActive) {
          newEntity.integration = { id: '' } as Integration;
        }
        newEntity.startDate = isIntegrationActive
          ? getProcessorStartDate(entity.integration, null, undefined, nonWorkingDays, false)
          : nextBusinessDay;
        newEntity.fundingDate = today;
        newEntity.status = MerchantCashAdvanceStatus.Open;
        newEntity.refiLevel = (tiers.refi || 0) + 1;
        newEntity.fees = {};
        newEntity.commissions = {};
        newEntity.refis = [];
        newEntity.linkedApplicationId = '';
      } else {
        newEntity.status = entity.status;
        newEntity.linkedApplicationId = applicationId || '';
        newEntity.startDate = dayjs(entity.startDate).utc().toDate();
        newEntity.fundingDate = dayjs(entity.fundingDate).utc().toDate();
        newEntity.fees = get(entity, 'fees', []).reduce(
        (fees: { [id: string]: string }, fee: FundingFee) => ({
          ...fees,
          [fee.id]: {
            amount: fee.amount,
            comment: fee.comment,
            percents: (fee.amount / entity.amount) * 100,
            config: fee.config
          }
        }),
        {}
      );
      newEntity.commissions = entity?.commissions?.reduce(
        (commissions: { [id: string]: string }, fee: Commission) => ({
          ...commissions,
          [fee.id]: {
            amount: fee.amount,
            comment: fee.comment,
            percents: (fee.amount / entity.amount) * 100,
            config: fee.config
          }
        }),
        {}
      );
        newEntity.isRefi = Boolean(entity.refis?.length) || tiers.primaryType === McaDisplayAttributeType.REFI_LEVEL;
        newEntity.refis = ((entity.refis || []) as McaRefi[]).map((refi: McaRefi) => {
          const { id, amount, number, referenceMca, type, clearedBalance, pendingBalance, totalFees, merchantId, merchantLogo, merchantLegalName } = refi;
          return { id, amount, number, referenceMca, type, clearedBalance, pendingBalance, totalFees, merchantId, merchantLogo, merchantLegalName };
        }) as McaRefiDto[];
        const linkedRefiRowCount = entity.refis?.length || 0;
        newEntity.refiLevel = tiers.refi ?? (linkedRefiRowCount > 0 ? linkedRefiRowCount : null);
      }
    }

    return newEntity;
  };

  const { loading: feesLoading, data: feesData } = useConfigFeesQuery({
    variables: {},
    fetchPolicy: 'network-only'
  });
  const [configFees, setConfigFees] = useState<ConfigFee[]>([]);
  useEffect(() => setConfigFees((feesData?.configFees.data || []) as ConfigFee[]), [feesData?.configFees.data, feesLoading]);
  const [mcaFees, setMcaFees] = useState<(FundingFee | FeeCommissionFormRow)[]>([]);
  const [mcaCommissions, setMcaCommissions] = useState<(Commission | FeeCommissionFormRow)[]>([]);
  const lastInitializedKeyRef = useRef<string | null>(null);
  const initialFeeIdsRef = useRef<Set<string>>(new Set());
  const initialCommissionIdsRef = useRef<Set<string>>(new Set());

  const uniqueConfigFeesById = (configs: ConfigFee[]) =>
    configs.filter((cf, i, arr) => arr.findIndex((c) => (c.id || '') === (cf.id || '')) === i);

  const mergePresetWithSaved = <T extends { id: string; configId?: string; config?: ConfigFee | null }>(
    saved: T[],
    presetConfigs: ConfigFee[],
    hasConfigId: (r: T) => boolean,
    createRow: (cf: ConfigFee) => FeeCommissionFormRow
  ): (T | FeeCommissionFormRow)[] => {
    const savedWithConfig = saved.map((r) => {
      if (r.config) return r;
      if (r.configId) {
        const config = configFees.find((cf) => cf.id === r.configId);
        return { ...r, config } as T;
      }
      return r;
    });

    const savedByKey = Object.fromEntries(savedWithConfig.filter(hasConfigId).map((r) => [r.config!.id || '', r]));
    const matchedSavedIds = new Set<string>();

    const presetRows = presetConfigs.map((cf) => {
      const byId = savedByKey[cf.id || ''];
      if (byId) {
        matchedSavedIds.add(byId.id);
        return byId;
      }
      const byName = savedWithConfig.filter((r) => !hasConfigId(r)).find((r) => r.config?.name === cf.name);
      if (byName) {
        matchedSavedIds.add(byName.id);
        return { ...byName, config: cf } as T;
      }
      return createRow(cf);
    });

    const presetKeys = new Set(presetConfigs.map((cf) => cf.id || ''));
    const presetNames = new Set(presetConfigs.map((cf) => cf.name));
    const custom = savedWithConfig.filter(
      (r) =>
        !matchedSavedIds.has(r.id) &&
        (!r.config?.name || !presetNames.has(r.config.name) || (hasConfigId(r) && !presetKeys.has(r.config?.id || '')))
    );

    return [...custom.filter((r) => !hasConfigId(r)), ...custom.filter(hasConfigId), ...presetRows];
  };

  const buildMergedFeesAndCommissions = (sourceEntity: { fundingFee?: FundingFee[]; commissions?: Commission[] } | null) => {
    const savedFees = sourceEntity?.fundingFee || [];
    const savedComms = sourceEntity?.commissions || [];
    const baseAmount = values?.amount || 0;
    const mergedFees = mergePresetWithSaved(
      savedFees,
      uniqueConfigFeesById(getConfigFees(configFees)),
      (f) => f.config?.id != null,
      (cf) => createPresetFeeCommissionRow(cf, baseAmount)
    );
    const mergedCommissions = mergePresetWithSaved(
      savedComms,
      uniqueConfigFeesById(getConfigCommissions(configFees)),
      (c) => c.config?.id != null,
      (cf) => createPresetFeeCommissionRow(cf, baseAmount, { isPaid: false })
    );
    return { mergedFees, mergedCommissions };
  };

  useEffect(() => {
    if (!configFees.length) return;
    const key = isFundingPipeline && offerId ? `offer-${offerId}` : `mca-${entity?.id || 'new'}`;
    if (lastInitializedKeyRef.current === key) return;
    lastInitializedKeyRef.current = key;

    const apply = (
      fees: (FundingFee | FeeCommissionFormRow)[],
      comms: (Commission | FeeCommissionFormRow)[]
    ) => {
      setMcaFees(fees);
      setMcaCommissions(comms);
      initialFeeIdsRef.current = new Set(fees.map((f) => f.id));
      initialCommissionIdsRef.current = new Set(comms.map((c) => c.id));
      const amount = values?.amount || 0;
      setFieldValue('fees', fees.reduce((acc, fee) => ({
        ...acc,
        [fee.id]: { amount: fee.amount, percents: amount ? (Number(fee.amount) / amount) * 100 : 0, comment: fee.comment || '', config: fee.config }
      }), {}));
      setFieldValue('commissions', comms.reduce((acc, c) => ({
        ...acc,
        [c.id]: { amount: c.amount, percents: amount ? (Number(c.amount) / amount) * 100 : 0, comment: c.comment || '', config: c.config, isPaid: (c as Commission)?.isPaid || false }
      }), {}));
    };

    if (isFundingPipeline && offerId) {
      if (entity && (entity.fundingFee?.length || entity.commissions?.length)) {
        apply(
          addResolvedConfigToItems(entity.fundingFee || [], configFees),
          addResolvedConfigToItems(entity.commissions || [], configFees)
        );
      } else {
        const { mergedFees, mergedCommissions } = buildMergedFeesAndCommissions(null);
        apply(mergedFees, mergedCommissions);
      }
    } else if (entity?.id && !isRefiMode) {
      apply(entity.fundingFee || [], entity.commissions || []);
    } else {
      const { mergedFees, mergedCommissions } = buildMergedFeesAndCommissions(null);
      apply(mergedFees, mergedCommissions);
    }
  }, [entity, configFees, isFundingPipeline, offerId]);


  const handleDeleteFee = async (fee: FundingFee | FeeCommissionFormRow) => {
    if (!isCreating && fee.id) {
      try {
        await deleteFee({
          variables: {
            id: fee.id,
            mcaId: entity.id
          }
        });
        dispatch(
          openSnackbar({
            open: true,
            message: `FundingFee ${fee.comment} has been deleted`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: true
          })
        );
      } catch (ignore) { }
    }
    setMcaFees(mcaFees.filter((f) => f.id !== fee.id));
    const updatedFees = { ...values.fees };
    delete updatedFees[fee.id];
    setFieldValue('fees', updatedFees);
  };
  const handleAddFee = () => {
    const baseConfig = configFees.find((cf) => cf.name === ConfigFeeName.OtherFee)!;
    const newFee = {
      id: uuid4(),
      amount: 0,
      comment: '',
      config: { ...baseConfig, isPercentage: false }
    } as FundingFee;
    setMcaFees([...mcaFees, newFee]);
    setFieldValue(`fees.${newFee.id}`, { amount: 0, percents: 0, comment: '', config: newFee.config });
  };

  const handleDeleteCommission = async (fee: Commission | FeeCommissionFormRow) => {
    if (!isCreating && fee.id) {
      try {
        await deleteCommission({
          variables: {
            id: fee.id,
            mcaId: entity.id
          }
        });
        dispatch(
          openSnackbar({
            open: true,
            message: `Commission ${fee.comment} has been deleted`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: true
          })
        );
      } catch (ignore) { }
    }
    setMcaCommissions(mcaCommissions.filter((f) => f.id !== fee.id));
    const updatedCommissions = { ...values.commissions };
    delete updatedCommissions[fee.id];
    setFieldValue('commissions', updatedCommissions);
  };

  const toggleCommissionStatus = async (com: Commission, isPaid: boolean) => {
    if (!isCreating && com.id) {
      await saveCommission({
        variables: {
          id: com.id!,
          mcaId: entity.id,
          commission: {
            isPaid: isPaid,
            amount: new Decimal(parseFloat(String(com.amount))).toNumber(),
            comment: com.comment
          }
        },
        refetchQueries: [{ query: CommissionsForMcaDocument, variables: { mcaId: entity.id } }]
      });
      dispatch(
        openSnackbar({
          open: true,
          message: `Commission ${com.comment} has been updated`,
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
    }
    setMcaCommissions(
      mcaCommissions.map((f) => {
        let updatedCom = { ...f };
        if (f.id === com.id) {
          updatedCom.isPaid = isPaid;
          updatedCom.amount = new Decimal(parseFloat(String(com.amount))).toNumber();
          updatedCom.comment = com.comment;
        }
        return updatedCom;
      })
    );
  };

  const handleAddCommission = () => {
    const baseConfig = configFees.find((cf) => cf.name === ConfigFeeName.BrokerFee)!;
    const newCommission = {
      id: uuid4(),
      amount: 0,
      comment: 'Broker Fee',
      isPaid: false,
      config: { ...baseConfig, isPercentage: true }
    } as Commission;
    setMcaCommissions([...mcaCommissions, newCommission]);
    setFieldValue(`commissions.${newCommission.id}`, { amount: 0, percents: 0, comment: 'Broker Fee', config: newCommission.config, isPaid: false });
  };

  const findPresetConfig = (configId: string | undefined, configs: ConfigFee[]) =>
    configs.find((c) => (c.id || '') === (configId || ''));

  const resetFeesToDefault = async () => {
    try {
      const presetConfigs = getConfigFees(configFees);
      const initialIds = initialFeeIdsRef.current;
      const fundingAmount = values?.amount || 0;
      const defaultAmount = (cf: ConfigFee) =>
        cf.isPercentage ? (fundingAmount * (cf?.defaultAmount || 0)) / 100 : cf.defaultAmount || 0;
      const updatedExistingFees = mcaFees.map((fee) => {
        if (!initialIds.has(fee.id)) return fee;
        const cf = fee.config ? findPresetConfig(fee.config.id, presetConfigs) : undefined;
        if (!cf) return fee;
        return { ...fee, amount: defaultAmount(cf), comment: cf.defaultComment || '' };
      });
      const currentInitialFeeConfigIds = new Set(
        mcaFees.filter((fee) => initialIds.has(fee.id) && fee.config?.id).map((fee) => fee.config!.id!)
      );
      const restoredFees = presetConfigs
        .filter((cf) => !currentInitialFeeConfigIds.has(cf.id || ''))
        .map((cf) => createPresetFeeCommissionRow(cf, fundingAmount));
      restoredFees.forEach((f) => initialFeeIdsRef.current.add(f.id));

      const updatedFees = [...updatedExistingFees, ...restoredFees];
      setMcaFees(updatedFees);
      setFieldValue('fees', updatedFees.reduce((acc, fee) => ({
        ...acc,
        [fee.id]: {
          amount: fee.amount,
          percents: fundingAmount ? (Number(fee.amount) / fundingAmount) * 100 : 0,
          comment: fee.comment || '',
          config: fee.config
        }
      }), {}));
      if (!isCreating && entity?.id) {
        for (const fee of restoredFees) {
          const cf = fee.config ? findPresetConfig(fee.config.id, presetConfigs) : undefined;
          if (!cf || Number(fee.amount) === 0) continue;
          await saveFee({
            variables: { id: fee.id, mcaId: entity.id, fee: { amount: fee.amount, comment: fee.comment || '', configId: cf.id } }
          });
        }
      }
      dispatch(openSnackbar({ open: true, message: 'Fees reset to default successfully.', variant: 'alert', alert: { color: 'success' }, close: true }));
    } catch (error) {
      dispatch(openSnackbar({ open: true, message: 'Error resetting fees to default.', variant: 'alert', alert: { color: 'error' }, close: true }));
    }
  };

  const resetCommissionsToDefault = async () => {
    try {
      const presetConfigs = getConfigCommissions(configFees);
      const initialIds = initialCommissionIdsRef.current;
      const fundingAmount = values?.amount || 0;
      const defaultAmount = (cf: ConfigFee) =>
        cf.isPercentage ? (fundingAmount * (cf?.defaultAmount || 0)) / 100 : cf.defaultAmount || 0;
      const updatedExistingCommissions = mcaCommissions.map((commission) => {
        if (!initialIds.has(commission.id)) return commission;
        const cf = commission.config ? findPresetConfig(commission.config.id, presetConfigs) : undefined;
        if (!cf) return commission;
        return { ...commission, amount: defaultAmount(cf), comment: cf.defaultComment || '' };
      });
      const currentInitialCommissionConfigIds = new Set(
        mcaCommissions.filter((c) => initialIds.has(c.id) && c.config?.id).map((c) => c.config!.id!)
      );
      const restoredCommissions = presetConfigs
        .filter((cf) => !currentInitialCommissionConfigIds.has(cf.id || ''))
        .map((cf) => createPresetFeeCommissionRow(cf, fundingAmount, { isPaid: false }));
      restoredCommissions.forEach((c) => initialCommissionIdsRef.current.add(c.id));

      const updatedCommissions = [...updatedExistingCommissions, ...restoredCommissions];
      setMcaCommissions(updatedCommissions);
      setFieldValue('commissions', updatedCommissions.reduce((acc, c) => ({
        ...acc,
        [c.id]: {
          amount: c.amount,
          percents: fundingAmount ? (Number(c.amount) / fundingAmount) * 100 : 0,
          comment: c.comment || '',
          config: c.config,
          isPaid: (c as Commission)?.isPaid || false
        }
      }), {}));
      if (!isCreating && entity?.id) {
        for (const commission of restoredCommissions) {
          const cf = commission.config ? findPresetConfig(commission.config.id, presetConfigs) : undefined;
          if (!cf || Number(commission.amount) === 0) continue;
          await saveCommission({
            variables: {
              id: commission.id,
              mcaId: entity.id,
              commission: {
                isPaid: (commission as Commission)?.isPaid || false,
                amount: commission.amount,
                comment: commission.comment || '',
                configId: cf.id || configFees.find((c) => c.name === ConfigFeeName.OtherFee)?.id
              }
            },
            refetchQueries: [{ query: CommissionsForMcaDocument, variables: { mcaId: entity.id } }]
          });
        }
      }
      dispatch(openSnackbar({ open: true, message: 'Commissions reset to default successfully.', variant: 'alert', alert: { color: 'success' }, close: true }));
    } catch (error) {
      dispatch(openSnackbar({ open: true, message: 'Error resetting commissions to default.', variant: 'alert', alert: { color: 'error' }, close: true }));
    }
  };

  const McaSchema = Yup.object().shape({
    merchant: Yup.object().shape({
      id: Yup.string().max(255).required('Merchant is required')
    }).required('Merchant is required'),
    company: Yup.object().shape({
      id: Yup.string().max(255).required('Company is required')
    }).required('Company is required'),
    integration: Yup.object().shape({
      id: Yup.string().max(255).required('Integration is required')
    }).required('Integration is required'),
    fundingDate: Yup.date()
      .typeError('Must be a valid date')
      .required('Funding date is required'),
    startDate: Yup.date()
      .notRequired()
      .when(['integration'], {
        is: (integration: Integration | null) =>
          integration != null &&
          isCreating &&
          !values.isImport &&
          [IntegrationType.GoAch, IntegrationType.GoAchBot, IntegrationType.DevNull].includes(
            integrationsData?.integrations.data.find(({ id }) => id === integration.id)?.type || IntegrationType.DevNull
          ),
        then: () =>
          Yup.date()
            .typeError('Must be a valid date')
            .test('is-today-or-later', 'Must be current business day or later', function (value) {
              const selectedDate = dayjs(value);
              const minDate = dayjs(getNextBusinessDay(today));
              return selectedDate.isSameOrAfter(minDate, 'day');
            })
            .required('Start date is required')
      })
      .when(['integration'], {
        is: (integration: Integration | null) =>
          integration != null &&
          isCreating &&
          !values.isImport &&
          [IntegrationType.AchProc, IntegrationType.ClearAchBot].includes(
            integrationsData?.integrations.data.find(({ id }) => id === integration.id)?.type || IntegrationType.DevNull
          ),
        then: () =>
          Yup.date()
            .typeError('Must be a valid date')
            .test('is-tomorrow-or-later', 'Must be next business day or later', function (value) {
              const selectedDate = dayjs(value);
              const minDate = dayjs(nextBusinessDay);
              return selectedDate.isSameOrAfter(minDate, 'day');
            })
            .required('Start date is required')
      }),
    amount: Yup.number().min(1, 'Funding amount is required').required('Funding amount is required'),
    term: Yup.number().min(1, 'Term is required').required('Term is required'),
    factorRate: Yup.number().min(1, 'Factor rate is required').required('Factor rate is required'),
    refiLevel: Yup.number()
      .nullable()
      .when('isRefi', {
        is: true,
        then: (schema) =>
          schema
            .required('REFI # is required')
            .integer('REFI # must be a whole number')
            .min(1, 'REFI # must be between 1 and 99')
            .max(99, 'REFI # must be between 1 and 99'),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    stackLevel: Yup.number()
      .nullable()
      .when('isRefi', {
        is: false,
        then: (schema) =>
          schema.test('stack-level', 'Type must be 1–9', function (v) {
            if (this.parent.reverseFunding) return v == null;
            return v == null || (Number.isInteger(v) && v >= 1 && v <= 9);
          }),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
    reverseFunding: Yup.boolean(),
    accountNumber: Yup.string().when(['integration'], {
      is: (integration: Integration) => isCreating,
      then: () =>
        Yup.string()
          .matches(/[0-9]+/gi, 'Account number is invalid')
          .min(6, 'Account number is invalid')
          .max(17, 'Account number is invalid')
          .required('Account number is required')
    }),
    routingNumber: Yup.string().when(['integration'], {
      is: (integration: Integration) => isCreating,
      then: () =>
        Yup.string()
          .matches(/[0-9]+/gi, 'Routing number is invalid')
          .test('routing-number-validation', 'Checksum of routing number is invalid', isValidRoutingNumber)
          .required('Routing number is required')
    }),
    fees: Yup.lazy((fees, options) =>
      Yup.object().shape(
        Object.entries(fees).reduce(
          (shape, [id, fee]) => ({
            ...shape,
            [id]: Yup.object().shape({
              amount: Yup.number().required(`Required`)
            })
          }),
          {}
        )
      )
    ),
    commissions: Yup.lazy((commissions, options) =>
      Yup.object().shape(
        Object.entries(commissions || []).reduce(
          (shape, [id, fee]) => ({
            ...shape,
            [id]: Yup.object().shape({
              amount: Yup.number().required(`Required`)
            })
          }),
          {}
        )
      )
    ),
    funder: Yup.object()
      .nullable()
      .notRequired()
      .shape({
        id: Yup.string().test('unique-funder', 'This underwriter is already selected as other underwriter', function (value) {
          if (!value) return true;
          const validationContext = this as Yup.TestContext & {
            from?: { value?: { secondaryFunders?: Values['secondaryFunders'] } }[];
          };
          const secondaryFunders = validationContext.from?.[1]?.value?.secondaryFunders || {};
          const usedIds = Object.values(secondaryFunders)
            .map((f) => f.lender?.id)
            .filter(Boolean);
          return !usedIds.includes(value);
        })
      })
  });

  const { data: integrationsData } = useIntegrationsQuery({
    variables: {},
    fetchPolicy: 'network-only'
  });

  const [loadApplicationsForMerchant, { data: applicationsForMerchantData }] = useApplicationsForMerchantLazyQuery({ fetchPolicy: 'network-only' });
  const [appLinkError, setAppLinkError] = useState<string | null>(null);

  const [confirmAccountOpen, setConfirmAccountOpen] = useState<boolean>(false);
  const finalCheckPassedRef = useRef<boolean>(false);

  const initialFormValues = useMemo(
    () => getInitialValues(entity as FormikValues | undefined),
    [entity?.id, isRefiMode, applicationId, entity ? mcaDisplayAttributeRows.length : 0]
  );

  const formik = useFormik<Values>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: McaSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isCreating && !finalCheckPassedRef.current) {
        setSubmitting(false);
        setConfirmAccountOpen(true);
        return;
      }
      try {
        const useFundingPipelineFlow = isFundingPipeline && !!applicationId;
        const refisForSubmit = values.isRefi ? (values.refis as McaRefiDto[]) : [];
        const typeAttr = values.reverseFunding ? 'V' : values.stackLevel ? `S${values.stackLevel}` : null;
        const displayAttributes: string[] =
          values.isRefi && values.refiLevel
            ? typeAttr ? [`R${values.refiLevel}`, typeAttr] : [`R${values.refiLevel}`]
            : typeAttr ? [typeAttr] : [];
        const baseVariables = {
          merchantId: values?.merchant ? (values.merchant as Company).id! : '',
          integrationId: values.integration.id,
          lenderId: values.funder ? (values?.funder as Underwriter).id : '',
          secondaryFunderIds: Object.values(values.secondaryFunders || {})
            .map((f) => f.lender?.id)
            .filter(Boolean),
          brokerId: values.broker ? (values?.broker as User).id : '',
          managerId: values.manager ? (values?.manager as User).id : '',
          legalId: values.legal ? (values?.legal as User).id : '',
          dto: {
            id: (entity && !isRefiMode) ? entity.id : null,
            companyId: values.company?.id,
            status: values.status,
            amount: new Decimal(values.amount).toNumber(),
            factorRate: new Decimal(values.factorRate).toNumber(),
            term: new Decimal(values.term).toNumber(),
            startDate: dayjs(values.startDate).set('hour', 12).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss[Z]'),
            fundingDate: dayjs(values.fundingDate).set('hour', 12).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss[Z]'),
            accountNumber: values.accountNumber,
            routingNumber: values.routingNumber,
            refis: refisForSubmit,
            displayAttributes
          }
        };

        let mca: MerchantCashAdvance;

        if (useFundingPipelineFlow) {
          const result = await saveFundingPipelineMca({
            variables: { ...baseVariables, applicationId: applicationId!, offerId: offerId! }
          });
          mca = result.data?.saveFundingPipelineMca as MerchantCashAdvance;
        } else {
          const result = await saveMerchantCashAdvance({ variables: { ...baseVariables } });
          mca = result.data?.saveMerchantCashAdvance as MerchantCashAdvance;
          if (mca && (values.linkedApplicationId || (!isCreating && initialFormValues.linkedApplicationId))) {
            await linkActiveOfferToMca({ variables: { mcaId: mca.id, applicationId: values.linkedApplicationId || '' }, refetchQueries: [OfferByMcaIdDocument] });
          }
        }
          const [fees] = await Promise.all([
            Promise.all(
              Object.entries(values.fees).map(([id, { amount, comment, config }]) => {
                const feeAmount = new Decimal(parseFloat(String(amount))).toNumber();
                const feeId = isFundingPipeline ? uuid4() : id;
                return saveFee({
                  variables: {
                    id: feeId,
                    mcaId: mca.id,
                    fee: {
                      amount: feeAmount,
                      comment,
                      configId: config?.id
                    }
                  }
                }).then((res) => res.data?.saveFee as FundingFee);
              })
            ),
            Promise.all(
              Object.entries(values.commissions).map(([id, { amount, comment, config }]) => {
                const commissionId = isFundingPipeline ? uuid4() : id;
                return saveCommission({
                  variables: {
                    id: commissionId,
                    mcaId: mca.id,
                    commission: {
                      isPaid: mcaCommissions.find((f) => f.id === id)?.isPaid,
                      amount: new Decimal(parseFloat(String(amount))).toNumber(),
                      comment,
                      configId: config?.id || configFees.find((cf) => cf.name === ConfigFeeName.OtherFee)?.id
                    }
                  }
                }).then((res) => res.data?.saveCommission as Commission)
              })
            )
          ]);
          if (isCreating) {
            const recurrencyResult = await saveRecurrency({
              variables: {
                id: uuid4(),
                mcaId: mca.id,
                integrationId: values.integration.id,
                dto: {
                  state: values.state,
                  amount: mca.amount * mca.factorRate,
                  dailyPayment: (mca.amount * mca.factorRate) / mca.term,
                  description: 'MCA PMT',
                  term: IntegrationType.AchProc === values.integration.type ? mca.term * 2 : mca.term,
                  startDate: dayjs(values.startDate).set('hour', 12).set('minute', 0).set('second', 0).format('YYYY-MM-DDTHH:mm:ss[Z]'),
                  routingNumber: values.routingNumber,
                  accountNumber: values.accountNumber
                }
              }
            });
            const recurrency = requireMutationData(recurrencyResult, (data) => data?.saveRecurrency, 'Recurrence was not saved.');
            mca.recurrencies = [recurrency as Recurrency];
          }
          onSave(mca, isCreating);
        if (!isCreating) {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Cash Advance updated successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        } else {
          dispatch(
            openSnackbar({
              open: true,
              message: 'Cash Advance added successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: true
            })
          );
        }
      } catch (error) {
        dispatch(
          openSnackbar({
            open: true,
            message: getErrorMessage(error, 'Failed to save cash advance.'),
            variant: 'alert',
            alert: {
              color: 'error'
            },
            close: true
          })
        );
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const merchantLegalName = (formik.values.merchant as Company)?.legalName;
    if (merchantLegalName) {
      loadApplicationsForMerchant({ variables: { merchantLegalName } });
    }
    if (isCreating && !isFundingPipeline && !isRefiMode) {
      formik.setFieldValue('linkedApplicationId', '');
      setAppLinkError(null);
      resetOfferFields();
    }
  }, [(formik.values.merchant as Company)?.id]);

  const applyOfferToForm = (offer: Partial<Offer>) => {
    formik.setFieldValue('company', offer.company?.id ? offer.company : { id: '' });
    formik.setFieldValue('integration', offer.integration?.id ? offer.integration : null);
    formik.setFieldValue('broker', offer.broker?.id ? offer.broker : {});
    formik.setFieldValue('manager', offer.manager?.id ? offer.manager : {});
    formik.setFieldValue('funder', offer.funder?.id ? offer.funder : {});
    const secondaryFunders = (offer.secondaryFunders || []).reduce(
      (acc, funder) => ({ ...acc, [uuid4()]: { lender: funder } }),
      {} as Values['secondaryFunders']
    );
    formik.setFieldValue('secondaryFunders', secondaryFunders);
    formik.setFieldValue('amount', offer.fundingAmount != null ? String(offer.fundingAmount) : '');
    formik.setFieldValue('factorRate', offer.factorRate != null ? String(offer.factorRate) : '');
    formik.setFieldValue('term', offer.term != null ? String(offer.term) : '');
  };

  const resetOfferFields = () => {
    formik.setFieldValue('company', { id: '' });
    formik.setFieldValue('integration', { id: '' });
    formik.setFieldValue('broker', {});
    formik.setFieldValue('manager', {});
    formik.setFieldValue('funder', {});
    formik.setFieldValue('secondaryFunders', {});
    formik.setFieldValue('amount', 1000);
    formik.setFieldValue('factorRate', 1.499);
    formik.setFieldValue('term', 10);
  };

  useEffect(() => {
    if (!isCreating || isFundingPipeline || isRefiMode) return;
    const linkedAppId = formik.values.linkedApplicationId;
    if (!linkedAppId || !applicationsForMerchantData?.applicationsForMerchant) return;
    const app = applicationsForMerchantData.applicationsForMerchant.find((a) => a.id === linkedAppId);
    if (!app) return;
    const offers = app.offers || [];
    const activeOffer = offers.find((o) => o?.isSelected && o?.isActive) || null;
    if (!activeOffer) return;
    applyOfferToForm(activeOffer as Partial<Offer>);
  }, [applicationsForMerchantData]);

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, values, handleBlur, setFieldValue, submitForm, validateForm, setErrors, setTouched } = formik;
  const isRefiOrCreating = values.isRefi || (values.refis?.length | 0) > 0;
  const setMcaTypeDisabled = (values.refis?.length | 0) > 0;
  const displayRefiLevel = isRefiOrCreating;

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (appLinkError) return;
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      formik.setErrors(validationErrors);
      const touchedFields = setNestedObjectValues(validationErrors, true) as any;
      if (validationErrors.integration) {
        touchedFields.integration = validationErrors.integration.id
            ? { id: true }
            : true;
      }
      formik.setTouched({ ...formik.touched, ...touchedFields }, true);
      formik.setSubmitting(false);
      return;
    }
    formik.submitForm();
  };

  const [activeField, setActiveField] = useState<string>('');

  const getPayback = () => {
    return values.amount * values.factorRate;
  };

  const getNetAmount = () => {
    return values.amount - getTotalFeeAmount();
  };

  const getTotalFeeAmount = () => {
    if (!values?.fees) return 0;
    return Object.entries(values.fees)
      .map(([configId, { amount, percents, comment }]) => {
        if (!configFees || typeof configFees.find === 'undefined') {
          return parseFloat(String(amount));
        }
        const config = configFees.find((cf) => cf.id === configId);
        return (config?.isPercentage ? (values.amount / 100) * parseFloat(String(amount)) : parseFloat(String(amount))) || 0;
      })
      .reduce((a, b) => a + b, 0);
  };

  const getTotalFeePercent = () => {
    return getTotalFeeAmount() ? (getTotalFeeAmount() / values.amount) * 100 : 0;
  };

  const getTotalCommissionAmount = () => {
    if (!values?.commissions) return 0;
    return Object.entries(values?.commissions)
      .map(([configId, { amount, percents, comment }]) => {
        if (!configFees || typeof configFees.find === 'undefined') {
          return parseFloat(String(amount));
        }
        const config = configFees.find((cf) => cf.id === configId);
        return (config?.isPercentage ? (values.amount / 100) * parseFloat(String(amount)) : parseFloat(String(amount))) || 0;
      })
      .reduce((a, b) => a + b, 0);
  };

  const getTotalCommissionPercent = () => {
    return getTotalCommissionAmount() ? (getTotalCommissionAmount() / values.amount) * 100 : 0;
  };

  return (
    <>
      {isCreating && (
        <AddMerchantCashAdvanceConfirm
          open={confirmAccountOpen}
          handleConfirmDone={(res) => {
            setConfirmAccountOpen(false);
            finalCheckPassedRef.current = res;
            if (res) {
              submitForm();
            }
          }}
          data={values}
        />
      )}
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: '1px solid {theme.palette.divider}' }}
      >
        <Grid item>
          <DialogTitle>{isCreating ? 'New Cash Advance' : 'Edit Cash Advance #' + entity?.number}</DialogTitle>
        </Grid>
        <Grid item sx={{ mr: 1.5 }}>
          <IconButton color="secondary" onClick={handleDrawerOpen}>
            <CloseOutlined />
          </IconButton>
        </Grid>
      </Grid>
      <FormikProvider value={formik}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} lg={12}>
                  <Stack direction={'row'}>
                    <Stack
                      sx={{
                        width: 170,
                        minWidth: 170,
                        maxWidth: 170,
                        borderRadius: 0,
                        borderLeft: '1px solid',
                        borderBottom: '1px solid',
                        borderTop: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel required={true} htmlFor="mac-entity-startDate" sx={{ color: '#B4ADE5' }}>
                          Start Date
                        </InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <DesktopDatePicker
                          value={values.startDate}
                          onChange={(value) => {
                            setStartDateChanged(true);
                            setFieldValue('startDate', dayjs(value).toDate() || nextBusinessDay);
                          }}
                          disablePast={isCreating && !values.isImport}
                          shouldDisableDate={(day) => {
                            if (nonWorkingDays.some((nwd) => dayjs(nwd.date).isSame(dayjs(day), 'day'))) {
                              return true;
                            }
                            return [6, 0].includes(day.getDay());
                          }}
                          slotProps={{
                            textField: {
                              sx: {
                                width: 140,
                                svg: { color: getStartDateColor(values.startDate) },
                                input: { color: getStartDateColor(values.startDate) },
                                label: { color: getStartDateColor(values.startDate) }
                              },
                              size: 'small',
                              error: Boolean(touched.startDate && errors.startDate),
                              helperText: touched.startDate && (errors.startDate ? <>{errors.startDate}</> : ''),
                              name: 'startDate',
                              onBlur: handleBlur
                            }
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 140,
                        minWidth: 140,
                        maxWidth: 140,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel required={true} htmlFor="mac-entity-amount" sx={{ color: '#36CFC9' }}>
                          Funding
                        </InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <TextField
                          size={'small'}
                          fullWidth
                          id="mac-entity-amount"
                          placeholder="Funding Amount"
                          {...getFieldProps('amount')}
                          onChange={(e) => {
                            setFieldValue('amount', e.target.value);
                            Object.entries(values.fees).forEach(([id, fee]) => {
                              if (fee?.config?.isPercentage) {
                                setFieldValue(`fees.${id}.amount`, (parseFloat(e.target.value) * values.fees[id].percents) / 100);
                              } else {
                                setFieldValue(`fees.${id}.percents`, (values.fees[id].amount / parseFloat(e.target.value)) * 100);
                              }
                            });
                            Object.entries(values.commissions).forEach(([id, fee]) => {
                              if (fee?.config?.isPercentage) {
                                setFieldValue(
                                  `commissions.${id}.amount`,
                                  (parseFloat(e.target.value) * values.commissions[id].percents) / 100
                                );
                              } else {
                                setFieldValue(
                                  `commissions.${id}.percents`,
                                  (values.commissions[id].amount / parseFloat(e.target.value)) * 100
                                );
                              }
                            });
                          }}
                          error={Boolean(touched.amount && errors.amount)}
                          helperText={touched.amount && errors.amount}
                          InputProps={{
                            startAdornment: '$',
                            inputComponent: CurrencyFormatterWithoutPrefix as any,
                            inputProps: { min: 1 }
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 90,
                        minWidth: 90,
                        maxWidth: 90,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel required={true} htmlFor="mac-entity-factorRate" sx={{ color: '#36CFC9' }}>
                          Factor
                        </InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <TextField
                          size={'small'}
                          fullWidth
                          id="mac-entity-factorRate"
                          placeholder="Enter Factor Rate"
                          {...getFieldProps('factorRate')}
                          value={values.factorRate}
                          onChange={(e) => setFieldValue('factorRate', e.target.value)}
                          error={Boolean(touched.factorRate && errors.factorRate)}
                          helperText={touched.factorRate && errors.factorRate}
                          InputProps={{
                            inputComponent: NumberFormatterWithoutPrefix as any,
                            inputProps: { min: 0 }
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 150,
                        minWidth: 150,
                        maxWidth: 150,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel sx={{ color: '#36CFC9' }}>Payback</InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <Chip
                          sx={{ marginTop: 0.5, backgroundColor: theme.palette.darkcyan.main, color: '#FFFFF' }}
                          label={<CurrencyText value={getPayback()} />}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 100,
                        minWidth: 100,
                        maxWidth: 100,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel required={true} htmlFor="mac-entity-term" sx={{ color: '#36CFC9' }}>
                          Term
                        </InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <TextField
                          size={'small'}
                          fullWidth
                          id="mac-entity-term"
                          placeholder="Enter Term"
                          {...getFieldProps('term')}
                          error={Boolean(touched.term && errors.term)}
                          helperText={touched.term && errors.term}
                          InputProps={{
                            endAdornment: 'D',
                            inputComponent: PercentFormatterWithoutPrefix as any,
                            inputProps: { min: 1 },
                            onChange: (e) => {
                              setFieldValue('term', e.target.value);
                            }
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 180,
                        minWidth: 180,
                        maxWidth: 180,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel>Pmt</InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <Chip
                          sx={{ marginTop: 0.5 }}
                          color={'default'}
                          label={<CurrencyText value={getPayback() / values.term} />}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 390,
                        minWidth: 390,
                        maxWidth: 390,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderLeft: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel required={true} sx={{ color: '#52C41A' }}>
                          Funding Fees
                        </InputLabel>
                      </Stack>
                      <Stack sx={{ padding: 2, pb: 1 }}>
                        <Stack direction={'row'} spacing={1} alignItems="center">
                          <Stack alignItems="center" sx={{ width: 135 }}>
                            <Chip
                              sx={{ backgroundColor: theme.palette.forestgreen.main, color: 'white' }}
                              label={<CurrencyText value={getTotalFeeAmount()} />}
                              size="small"
                            />
                          </Stack>
                          <Stack alignItems="center" sx={{ width: 75 }}>
                            <Chip
                              color={'default'}
                              label={<PercentText value={getTotalFeePercent()} />}
                              size="small"
                            />
                          </Stack>
                          <Box sx={{ flex: 1 }} />
                          {isCreating && (
                            <CustomTooltip title="Set Fees">
                              <Button onClick={resetFeesToDefault} variant="contained" size="small" sx={{ fontSize: '12px', minWidth: 'auto' }}>
                                <DollarOutlined />
                              </Button>
                            </CustomTooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 390,
                        minWidth: 390,
                        maxWidth: 390,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderLeft: '1px solid',
                        borderTop: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel sx={{ color: '#655ac8' }}>Commissions</InputLabel>
                      </Stack>
                      <Stack sx={{ padding: 2, pb: 1 }}>
                        <Stack direction={'row'} spacing={1} alignItems="center">
                          <Stack alignItems="center" sx={{ width: 105 }}>
                            <Chip
                              color={'primary'}
                              label={<CurrencyText value={getTotalCommissionAmount()} />}
                              size="small"
                            />
                          </Stack>
                          <Stack alignItems="center" sx={{ width: 75 }}>
                            <Chip
                              color={'default'}
                              label={<PercentText value={getTotalCommissionPercent()} />}
                              size="small"
                            />
                          </Stack>
                          <Box sx={{ flex: 1 }} />
                          {isCreating && (
                            <CustomTooltip title="Set Commissions">
                              <Button onClick={resetCommissionsToDefault} variant="contained" size="small" sx={{ fontSize: '12px', minWidth: 'auto' }}>
                                <PercentageOutlined />
                              </Button>
                            </CustomTooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                    {values.isRefi && (
                      <Stack
                        sx={{
                          width: 279,
                          minWidth: 279,
                          maxWidth: 279,
                          borderRadius: 0,
                          borderBottom: '1px solid',
                          borderLeft: '1px solid',
                          borderTop: '1px solid',
                          borderColor: '#656974'
                        }}
                      >
                        <Stack alignItems="center" sx={rowSx}>
                          <InputLabel htmlFor="mac-entity-term" sx={{ color: '#FF4D4F' }}>
                            Refi
                          </InputLabel>
                        </Stack>
                        <Stack alignItems="center" sx={{ padding: 2 }}>
                          <Chip
                            sx={{ marginTop: 0.5 }}
                            color={'error'}
                            label={<CurrencyText value={getRefisTotal(values.refis)} />}
                            size="small"
                            variant="light"
                          />
                        </Stack>
                      </Stack>
                    )}
                    <Stack
                      sx={{
                        width: isRefiOrCreating ? 175 : 280,
                        minWidth: isRefiOrCreating ? 175 : 280,
                        maxWidth: isRefiOrCreating ? 175 : 280,
                        borderRadius: 0,
                        border: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" sx={rowSx}>
                        <InputLabel htmlFor="mac-entity-term" sx={{ color: '#597EF7' }}>
                          Net Wire
                        </InputLabel>
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <Chip
                          sx={{ marginTop: 0.5 }}
                          color={'info'}
                          label={<CurrencyText value={getNetAmount()} />}
                          size="small"
                          variant="filled"
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={'row'}>
                    <Stack
                      sx={{
                        width: 170,
                        minWidth: 170,
                        maxWidth: 170,
                        borderRadius: 0,
                        borderLeft: '1px solid',
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack alignItems="center" spacing={1} sx={{ padding: 2 }}>
                        <InputLabel required={true} htmlFor="mac-entity-fundingDate">
                          Funding Date
                        </InputLabel>
                        <DesktopDatePicker
                          value={values.fundingDate}
                          onChange={(value) => {
                            setFieldValue('fundingDate', dayjs(value).toDate() || today);
                          }}
                          disablePast={false}
                          shouldDisableDate={(day) => {
                            if (nonWorkingDays.some((nwd) => dayjs(nwd.date).isSame(dayjs(day), 'day'))) {
                              return true;
                            }
                            return [6, 0].includes(day.getDay());
                          }}
                          slotProps={{
                            textField: {
                              sx: {
                                width: 140
                              },
                              size: 'small',
                              error: Boolean(touched.fundingDate && errors.fundingDate),
                              helperText: touched.fundingDate && (errors.fundingDate ? <>{errors.fundingDate}</> : ''),
                              name: 'fundingDate',
                              onBlur: handleBlur
                            }
                          }}
                        />
                      </Stack>
                      <Stack alignItems="center" sx={{ padding: 2 }}>
                        <SetMcaType formik={formik} disabled={setMcaTypeDisabled} />
                      </Stack>
                      <Stack direction="row" alignItems="flex-start" justifyContent="center">
                        {displayRefiLevel && (
                          <McaRefiLevelField
                            refiLevel={values.refiLevel}
                            onChange={(level) => setFieldValue('refiLevel', level)}
                            onBlur={handleBlur}
                            error={Boolean(touched.refiLevel && errors.refiLevel)}
                            helperText={touched.refiLevel && errors.refiLevel}
                          />
                        )}
                        <McaStackLevelField
                          stackLevel={values.stackLevel}
                          reverseFunding={values.reverseFunding}
                          onChange={(next) => {
                            setFieldValue('stackLevel', next.stackLevel);
                            setFieldValue('reverseFunding', next.reverseFunding);
                          }}
                        />
                      </Stack>
                      <Stack alignItems="center" spacing={0.5} sx={{ px: 1, pb: 1 }}>
                        <InputLabel htmlFor="mac-entity-linked-app" sx={{ fontSize: '12px' }}>App#</InputLabel>
                        <Select
                          id="mac-entity-linked-app"
                          size="small"
                          displayEmpty
                          disabled={!(values.merchant as Company)?.id || isFundingPipeline}
                          value={values.linkedApplicationId}
                          onChange={(e) => {
                            const appId = e.target.value as string;
                            setFieldValue('linkedApplicationId', appId);
                            if (!appId) {
                              setAppLinkError(null);
                              if (isCreating && !isFundingPipeline && !isRefiMode) {
                                resetOfferFields();
                              }
                              return;
                            }
                            const app = applicationsForMerchantData?.applicationsForMerchant.find((a) => a.id === appId);
                            const linkedOffer = app?.offers?.find((o) => o.mcaId && o.mcaId !== entity?.id);
                            if (linkedOffer) {
                              setAppLinkError(`Application #${app!.number} is already linked to MCA #${linkedOffer.mca?.number ?? linkedOffer.mcaId}`);
                            } else {
                              setAppLinkError(null);
                            }
                            if (isCreating && !isFundingPipeline && !isRefiMode && app) {
                              const offers = app.offers || [];
                              const activeOffer = offers.find((o) => o?.isSelected && o?.isActive) || null;
                              if (activeOffer) applyOfferToForm(activeOffer as Partial<Offer>);
                            }
                          }}
                          sx={{ width: 140, '& .MuiSelect-select': { display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
                          renderValue={(val) => {
                            if (!val) return <span style={{ color: '#888' }}>-</span>;
                            const app = applicationsForMerchantData?.applicationsForMerchant.find((a) => a.id === val);
                            if (!app) return <span style={{ color: '#888' }}>-</span>;
                            return (
                              <Chip
                                sx={{
                                  borderRadius: '4px',
                                  backgroundColor: app.status?.color || '#999',
                                  height: '20px',
                                  textTransform: 'uppercase',
                                  color: theme.palette.aliceblue?.main,
                                  fontSize: '11px',
                                  '& .MuiChip-label': {
                                    WebkitTextFillColor: theme.palette.aliceblue?.main,
                                  }
                                }}
                                label={`${app.number} · ${getFormattedDateUTC(app.offers?.find((o) => o.mcaId === entity?.id)?.offerDate || app.offers?.find((o) => o.isSelected && o.isActive)?.offerDate || app.offerDate)}`}
                                size="small"
                              />
                            );
                          }}
                        >
                          <MenuItem value="" sx={{ justifyContent: 'center' }}>-</MenuItem>
                          {(applicationsForMerchantData?.applicationsForMerchant || []).map((app) => (
                            <MenuItem key={app.id} value={app.id} sx={{ justifyContent: 'center' }}>
                              <Chip
                                sx={{
                                  borderRadius: '4px',
                                  backgroundColor: app.status?.color || '#999',
                                  height: '20px',
                                  textTransform: 'uppercase',
                                  color: theme.palette.aliceblue?.main ?? '#fff',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                }}
                                label={`${app.number} · ${getFormattedDateUTC(app.offers?.find((o) => o.mcaId === entity?.id)?.offerDate || app.offers?.find((o) => o.isSelected && o.isActive)?.offerDate || app.offerDate)}`}
                                size="small"
                              />
                            </MenuItem>
                          ))}
                        </Select>
                        {appLinkError && (
                          <Typography variant="caption" color="error" sx={{ fontSize: '11px', textAlign: 'center' }}>
                            {appLinkError}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 315,
                        minWidth: 315,
                        maxWidth: 315,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack spacing={0.25}>
                        <InternalCompanyAutocomplete id={'mac-entity-company'} disabled={false} fundingCompany />
                        <MerchantAutocomplete id={'mac-entity-merchant'} disabled={!isCreating || isFundingPipeline || isRefiMode} formik={formik} />
                        <FunderAutocomplete formik={formik} id={'mac-entity-funder'} disabled={false} />
                        {/* Secondary funders section */}
                        <FieldArray
                          name={'secondaryFunders'}
                          children={(helpers) => (
                            <Stack spacing={0.25}>
                              {Object.entries(values.secondaryFunders || {}).map(([funderId, funder]) => (
                                <Stack key={`secondary-funder-row-${funderId}`} direction={'row'} spacing={0.25}>
                                  <Stack sx={{ width: '280px' }}>
                                    <SecondaryFunderAutocomplete
                                      key={funderId}
                                      id={`secondary-funder-${funderId}`}
                                      funderId={funderId}
                                      disabled={false}
                                      formik={formik}
                                      onDelete={() => {
                                        const newFunders = { ...values.secondaryFunders };
                                        delete newFunders[funderId];
                                        setFieldValue('secondaryFunders', newFunders);
                                      }}
                                    />
                                  </Stack>
                                  <IconButton
                                    sx={{ width: '30px', height: '30px' }}
                                    color="secondary"
                                    onClick={() => {
                                      const newFunders = { ...values.secondaryFunders };
                                      delete newFunders[funderId];
                                      setFieldValue('secondaryFunders', newFunders);
                                    }}
                                  >
                                    <DeleteOutlined />
                                  </IconButton>
                                </Stack>
                              ))}
                              <Stack sx={{ pl: 2, pr: 2 }} alignItems="flex-start">
                                <Button
                                  color="primary"
                                  variant={'text'}
                                  size={'small'}
                                  startIcon={<PlusCircleOutlined />}
                                  onClick={() => {
                                    setFieldValue(`secondaryFunders.${uuid4()}`, {
                                      lender: {} as Underwriter
                                    });
                                  }}
                                >
                                  Add Underwriter
                                </Button>
                              </Stack>
                            </Stack>
                          )}
                        />
                        <BrokerAutocomplete id={'mac-entity-broker'} disabled={false} />
                        <IsoManagerAutocomplete id={'mac-entity-manager'} disabled={false} />
                        <LegalUserAutocomplete id={'mac-entity-legal'} disabled={false} formik={formik} />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 346,
                        minWidth: 346,
                        maxWidth: 346,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack spacing={0.25}>
                        <IntegrationAutocomplete
                          id={'mac-entity-integration'}
                          formik={formik}
                          disabled={!isCreating}
                          onValueChanged={(integration) => {
                            setFieldValue(
                              'startDate',
                              getProcessorStartDate(integration, null, values.startDate, nonWorkingDays, startDateChanged)
                            );
                          }}
                        />
                        <Stack sx={{ px: 2 }} spacing={1}>
                        <Divider />
                          <MerchantBankAccountInput
                            merchant={values.merchant}
                            formik={formik}
                            isCreating={isCreating}
                            {...(entity && !isRefiMode ? {
                              accountNumber: entity.accountNumber || undefined,
                              routingNumber: entity.routingNumber || undefined
                            } : {})}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 390,
                        minWidth: 390,
                        maxWidth: 390,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack pt={2} pl={2} pr={2} pb={2}>
                        <FieldArray
                          name={'fees'}
                          children={(helpers) => (
                            <Stack spacing={2}>
                              {mcaFees.map((fee) => (
                                <Stack key={fee.id} direction={'row'} spacing={0.25}>
                                  <Stack direction={'row'} spacing={1}>
                                    <TextField
                                      size={'small'}
                                      fullWidth
                                      placeholder="Amount"
                                      disabled={fee.config?.isPercentage}
                                      {...getFieldProps(`fees.${fee.id}.amount`)}
                                      onFocus={(e) => setActiveField(e.target.name)}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setFieldValue(`fees.${fee.id}.amount`, val);
                                        if (activeField === `fees.${fee.id}.amount`) {
                                          setFieldValue(`fees.${fee.id}.percents`, (parseFloat(val) / values.amount) * 100);
                                        }
                                        setMcaFees((prev) =>
                                          prev.map((f) => (f.id === fee.id ? { ...f, amount: parseFloat(val) || 0 } : f))
                                        );
                                      }}
                                      error={Boolean(touched.fees && touched.fees[fee.id] && errors.fees && errors.fees[fee.id])}
                                      helperText={
                                        touched.fees && touched.fees[fee.id]?.amount && errors.fees && errors.fees[fee.id]?.amount
                                      }
                                      InputProps={{
                                        startAdornment: '$',
                                        inputComponent: CurrencyFormatterWithoutPrefix as any,
                                        inputProps: { min: 0 }
                                      }}
                                      sx={{ width: 135 }}
                                    />
                                    <TextField
                                      size={'small'}
                                      fullWidth
                                      placeholder="Percent"
                                      disabled={!fee.config?.isPercentage}
                                      {...getFieldProps(`fees.${fee.id}.percents`)}
                                      onFocus={(e) => setActiveField(e.target.name)}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const amount = (parseFloat(val) * values.amount) / 100;
                                        setFieldValue(`fees.${fee.id}.percents`, val);
                                        if (activeField === `fees.${fee.id}.percents`) {
                                          setFieldValue(`fees.${fee.id}.amount`, amount);
                                        }
                                        setMcaFees((prev) =>
                                          prev.map((f) => (f.id === fee.id ? { ...f, amount } : f))
                                        );
                                      }}
                                      error={Boolean(touched.fees && touched.fees[fee.id] && errors.fees && errors.fees[fee.id])}
                                      helperText={
                                        touched.fees && touched.fees[fee.id]?.percents && errors.fees && errors.fees[fee.id]?.percents
                                      }
                                      InputProps={{
                                        endAdornment: '%',
                                        inputComponent: PercentFormatterWithoutPrefix as any,
                                        inputProps: { min: 0 }
                                      }}
                                      sx={{ width: 75 }}
                                    />
                                    <TextField
                                      size={'small'}
                                      fullWidth
                                      placeholder="Comment"
                                      value={values.fees[fee.id]?.comment || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setFieldValue(`fees.${fee.id}.comment`, val);
                                        setMcaFees((prev) =>
                                          prev.map((f) => (f.id === fee.id ? { ...f, comment: val } : f))
                                        );
                                      }}
                                      error={Boolean(touched.fees && touched.fees[fee.id] && errors.fees && errors.fees[fee.id])}
                                      helperText={
                                        touched.fees && touched.fees[fee.id]?.comment && errors.fees && errors.fees[fee.id]?.comment
                                      }
                                      sx={{ width: 110 }}
                                    />
                                  </Stack>
                                  <IconButton sx={{ width: '30px', height: '30px' }} color="secondary" onClick={() => handleDeleteFee(fee)}>
                                    <DeleteOutlined />
                                  </IconButton>
                                </Stack>
                              ))}
                              <Stack alignItems="flex-start">
                                <Button
                                  color="primary"
                                  variant={'text'}
                                  size={'small'}
                                  startIcon={<PlusCircleOutlined />}
                                  onClick={handleAddFee}
                                >
                                  Add Funding Fee
                                </Button>
                              </Stack>
                            </Stack>
                          )}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 390,
                        minWidth: 390,
                        maxWidth: 390,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack pt={2} pl={2} pr={2} pb={2}>
                        <FieldArray
                          name={'commissions'}
                          children={(helpers) => (
                            <Stack spacing={2}>
                              <>
                                {mcaCommissions.map((fee) => {
                                  return (
                                    <Stack key={fee.id} direction={'row'} spacing={0.25}>
                                      <Stack direction={'row'} spacing={1}>
                                        <TextField
                                          size={'small'}
                                          fullWidth
                                          placeholder="Amount"
                                          disabled={fee.config?.isPercentage}
                                          {...getFieldProps(`commissions.${fee.id}.amount`)}
                                          onFocus={(e) => setActiveField(e.target.name)}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setFieldValue(`commissions.${fee.id}.amount`, val);
                                            if (activeField === `commissions.${fee.id}.amount`) {
                                              setFieldValue(
                                                `commissions.${fee.id}.percents`,
                                                (parseFloat(val) / values.amount) * 100
                                              );
                                            }
                                            setMcaCommissions((prev) =>
                                              prev.map((c) => (c.id === fee.id ? { ...c, amount: parseFloat(val) || 0 } : c))
                                            );
                                          }}
                                          error={Boolean(
                                            touched.commissions &&
                                            touched.commissions[fee.id] &&
                                            errors.commissions &&
                                            errors.commissions[fee.id]
                                          )}
                                          helperText={
                                            touched.commissions &&
                                            touched.commissions[fee.id]?.amount &&
                                            errors.commissions &&
                                            errors.commissions[fee.id]?.amount
                                          }
                                          InputProps={{
                                            startAdornment: '$',
                                            inputComponent: CurrencyFormatterWithoutPrefix as any,
                                            inputProps: { min: 0 }
                                          }}
                                          sx={{ width: 105 }}
                                        />
                                        <TextField
                                          size={'small'}
                                          fullWidth
                                          placeholder="Percent"
                                          disabled={!fee.config?.isPercentage}
                                          {...getFieldProps(`commissions.${fee.id}.percents`)}
                                          onFocus={(e) => setActiveField(e.target.name)}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const amount = (parseFloat(val) * values.amount) / 100;
                                            setFieldValue(`commissions.${fee.id}.percents`, val);
                                            if (activeField === `commissions.${fee.id}.percents`) {
                                              setFieldValue(`commissions.${fee.id}.amount`, amount);
                                            }
                                            setMcaCommissions((prev) =>
                                              prev.map((c) => (c.id === fee.id ? { ...c, amount } : c))
                                            );
                                          }}
                                          error={Boolean(
                                            touched.commissions &&
                                            touched.commissions[fee.id] &&
                                            errors.commissions &&
                                            errors.commissions[fee.id]
                                          )}
                                          helperText={
                                            touched.commissions &&
                                            touched.commissions[fee.id]?.percents &&
                                            errors.commissions &&
                                            errors.commissions[fee.id]?.percents
                                          }
                                          InputProps={{
                                            endAdornment: '%',
                                            inputComponent: PercentFormatterWithoutPrefix as any,
                                            inputProps: { min: 0 }
                                          }}
                                          sx={{ width: 75 }}
                                        />
                                        <Select
                                          size="small"
                                          fullWidth
                                          value={getFieldProps(`commissions.${fee.id}.comment`).value || ''}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setFieldValue(`commissions.${fee.id}.comment`, val);
                                            setMcaCommissions((prev) =>
                                              prev.map((c) => (c.id === fee.id ? { ...c, comment: val } : c))
                                            );
                                          }}
                                          onBlur={getFieldProps(`commissions.${fee.id}.comment`).onBlur}
                                          sx={{ width: 110 }}
                                        >
                                          <MenuItem value="Broker Fee">Broker Fee</MenuItem>
                                          <MenuItem value="Broker Fee In-house">Broker Fee In-house</MenuItem>
                                          <MenuItem value="ISO Fee">ISO Fee</MenuItem>
                                          <MenuItem value="ISO Fee In-house">ISO Fee In-house</MenuItem>
                                        </Select>
                                      </Stack>
                                      <Stack direction={'row'} spacing={0}>
                                        <IconButton
                                          sx={{ width: '30px', height: '30px' }}
                                          color="secondary"
                                          onClick={() => handleDeleteCommission(fee)}
                                        >
                                          <DeleteOutlined />
                                        </IconButton>
                                        {!fee.isPaid && (
                                          <CustomTooltip title="Set as Paid">
                                            <IconButton
                                              sx={{ width: '30px', height: '30px' }}
                                              color="error"
                                              onClick={(e: any) => {
                                                e.stopPropagation();
                                                void toggleCommissionStatus({ id: fee.id, ...values.commissions[fee.id] } as Commission, true).catch((error) =>
                                                  dispatch(
                                                    openSnackbar({
                                                      open: true,
                                                      message: getErrorMessage(error, 'Failed to update commission status.'),
                                                      variant: 'alert',
                                                      alert: { color: 'error' },
                                                      close: true
                                                    })
                                                  )
                                                );
                                              }}
                                            >
                                              <WalletOutlined twoToneColor={theme.palette.error.main} />
                                            </IconButton>
                                          </CustomTooltip>
                                        )}
                                        {fee.isPaid && (
                                          <CustomTooltip title="Set as Unpaid">
                                            <IconButton
                                              sx={{ width: '30px', height: '30px' }}
                                              color="success"
                                              onClick={(e: any) => {
                                                e.stopPropagation();
                                                void toggleCommissionStatus({ id: fee.id, ...values.commissions[fee.id] } as Commission, false).catch((error) =>
                                                  dispatch(
                                                    openSnackbar({
                                                      open: true,
                                                      message: getErrorMessage(error, 'Failed to update commission status.'),
                                                      variant: 'alert',
                                                      alert: { color: 'error' },
                                                      close: true
                                                    })
                                                  )
                                                );
                                              }}
                                            >
                                              <WalletOutlined twoToneColor={theme.palette.success.main} />
                                            </IconButton>
                                          </CustomTooltip>
                                        )}
                                      </Stack>
                                    </Stack>
                                  );
                                })}
                                <Stack alignItems="flex-start">
                                  <Button
                                    color="primary"
                                    variant={'text'}
                                    size={'small'}
                                    startIcon={<PlusCircleOutlined />}
                                    onClick={handleAddCommission}
                                  >
                                    Add Commission
                                  </Button>
                                </Stack>
                              </>
                            </Stack>
                          )}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      sx={{
                        width: 279,
                        minWidth: 279,
                        maxWidth: 279,
                        borderRadius: 0,
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderColor: '#656974'
                      }}
                    >
                      <Stack>
                        {isRefiOrCreating && (
                          <SetMcaRefis
                            formik={formik}
                            setMcaFees={setMcaFees}
                            mcaDisplayAttributes={mcaDisplayAttributeRows}
                            initialMca={isRefiMode ? entity : null}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item> </Grid>
                <Grid item>
                  <Stack direction={'row'} spacing={4}>
                    <AnimateButton>
                      <Button fullWidth color="error" startIcon={<CloseCircleOutlined />} onClick={handleDrawerOpen}>
                        Cancel
                      </Button>
                    </AnimateButton>
                    <AnimateButton>
                      <Button
                        startIcon={isCreating ? <FlagOutlined /> : <EditOutlined />}
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting || confirmAccountOpen}
                      >
                        {isCreating ? 'Start MCA' : 'Update MCA'}
                      </Button>
                    </AnimateButton>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default AddMerchantCashAdvance;
